import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Form, Formik } from 'formik';
import { useHistory } from 'react-router-dom';
import { Box, Button, Typography } from '@material-ui/core';

import {
  UpdateUserProfileMutation,
  UpdateUserProfileMutationVariables,
  useUpdateUserProfileMutation,
} from 'api';
import { ClientLogo, Layouts, Loaders, PaddedPaper, ValidatedTextFieldNext } from 'components';
import { useCapabilities, useCurrentUser, useProtectedClient } from 'hooks';
import { useUpdateCurrentUser } from 'updaters';
import { hasCapability } from 'helpers';
import { Terms } from 'features/auth';
import { getAppImage } from 'helpers';
import * as paths from 'paths';

import { OnboardingCreateSchema, OnboardingCreateValues } from '../validations';

import { useTrackingQuery } from 'app-react-query/hooks';
import { FunnelEvent } from 'analytics';

type Update = UpdateUserProfileMutation;
type UpdateVariables = UpdateUserProfileMutationVariables;

const OnboardingCreate: FC = () => {
  const history = useHistory();
  const client = useProtectedClient();
  const currentUser = useCurrentUser();
  const capabilities = useCapabilities();
  const { t } = useTranslation();

  const track = useTrackingQuery();

  const hasDepartmentsAndLocations =
    hasCapability(capabilities, 'Admin.Settings.Account.Function.Departments') &&
    hasCapability(capabilities, 'Admin.Settings.Account.Function.Locations');
  const updateOptions = useUpdateCurrentUser<Update, UpdateVariables>();
  const initialValues: OnboardingCreateValues = {
    full_name: currentUser.full_name || '',
    acceptTerms: false,
  };

  const { mutate, isLoading } = useUpdateUserProfileMutation({
    onSuccess: data => {
      track(FunnelEvent.SLNUSUMCU, data.updateUserProfile.id, {});
      hasDepartmentsAndLocations
        ? history.push(paths.onboardingIdentity)
        : history.push(paths.onboardingSocials);
    },
    ...updateOptions,
  });

  function handleSubmit(values: OnboardingCreateValues) {
    window.Intercom?.('update', {
      id: currentUser.id,
      name: values.full_name,
    });

    mutate({
      data: {
        id: currentUser.id,
        full_name: values.full_name,
        last_onboarding_step: 1,
      },
    });
  }

  function renderImage() {
    return <img width={500} height={500} {...getAppImage('illustrations/boat.png', 500)} />;
  }

  function renderMain() {
    return (
      <PaddedPaper disableBorder padding="64px 48px">
        <Formik
          onSubmit={handleSubmit}
          initialValues={initialValues}
          validationSchema={OnboardingCreateSchema}
        >
          <Layouts.OnboardingBox component={Form}>
            <Box textAlign="center">
              <ClientLogo client={client} />
            </Box>
            <Typography align="center" paragraph variant="h2">
              {t('onboarding:createYourAccountHeader')}
            </Typography>
            <Typography align="center" paragraph color="textSecondary">
              {t('onboarding:createYourAccountSubHead', { name: client.title })}
            </Typography>
            <Box paddingTop={3}>
              <ValidatedTextFieldNext
                autoFocus
                fullWidth
                name="full_name"
                label={t('onboarding:yourFullName')}
                placeholder={t('onboarding:yourFullName')}
              />
            </Box>
            <Box paddingTop={2}>
              <Terms client={client} />
            </Box>
            <Box paddingTop={3}>
              <Button
                fullWidth
                type="submit"
                size="large"
                variant="contained"
                color="primary"
                disabled={isLoading}
              >
                {isLoading ? <Loaders.Standard height={26} /> : t('common:continue')}
              </Button>
            </Box>
          </Layouts.OnboardingBox>
        </Formik>
      </PaddedPaper>
    );
  }

  return <Layouts.Onboarding image={renderImage()} main={renderMain()} />;
};

export default OnboardingCreate;
